// Buying a pass from the wallet at a venue (spec/15 P-06, P-10; ADR-0054 §6): the Buy button, its confirmation and what
// a refusal says, shared by a venue's page and Play. Paid from the player's wallet at that venue and by nothing else;
// where the wallet holds too little the card says to top up at the counter instead of offering Buy. The server prices
// and decides: the screen offers what `GET /v1/me/venues/{venueId}/passes` says, sends the total it showed, and one key
// per opened confirmation, so a double tap buys once.

import type { MyLiveSession, MyShopPass, MyVenuePassShop } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, RadioGroup, useToast } from '@clavian/ui-web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { queryKeys, useMyVenuePassShop } from '../api/queries';
import { useProblem } from '../api/use-problem';
import { formatterFor, shortDuration } from '../lib/format';
import { goToCheckout } from '../lib/payments';
import { SectionHead } from '../shell/feedback';

const newKey = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;

type Tender = 'wallet' | 'online';

/** Buy one pass, and refresh everything it changed: the wallet, the passes, the orders and the live session. */
function useBuyPass(venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation('player');
  const problem = useProblem();
  const { show } = useToast();
  return useMutation({
    mutationFn: ({ pass, key, tender }: { pass: MyShopPass; key: string; tender: Tender }) =>
      api.purchaseMyPass({
        path: { venueId, productId: pass.id },
        headerParams: { 'idempotency-key': key },
        body: { tender, expectedTotal: pass.total },
      }),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.myVenueShop(venueId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.wallets });
      await queryClient.invalidateQueries({ queryKey: queryKeys.passes });
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders });
      await queryClient.invalidateQueries({ queryKey: queryKeys.live });
    },
    onError: (error) => show({ title: problem.text(error, { fallback: t('shop.failure.generic') }), tone: 'danger' }),
  });
}

/**
 * The card's action: Buy, with a confirmation that says what leaves the wallet, or "Top up at the counter" when the
 * wallet here holds too little for it. Where the café takes online payments the confirmation also lets the player pay
 * online: they leave for the payment page and come back to `/payment-complete`.
 */
export function BuyPassAction({ shop, pass }: { readonly shop: MyVenuePassShop; readonly pass: MyShopPass }): ReactNode {
  const { t } = useTranslation('player');
  const { show } = useToast();
  const buy = useBuyPass(shop.venue.id);
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState(newKey);
  const walletOk = shop.walletEnabled && pass.affordable;
  const [tender, setTender] = useState<Tender>(walletOk ? 'wallet' : 'online');
  const fmt = formatterFor(pass.total.currency);

  if (!pass.affordable && !shop.onlinePayments) return <p className="pw-shop__topup">{t('shop.topUp')}</p>;

  const left = { amount: Math.max(0, shop.walletBalance.amount - pass.total.amount), currency: shop.walletBalance.currency };
  const paying: Tender = shop.onlinePayments ? tender : 'wallet';
  return (
    <>
      <Button
        variant="primary"
        size="sm"
        aria-label={t('shop.buyNamed', { name: pass.name })}
        onClick={() => {
          setKey(newKey());
          setTender(walletOk ? 'wallet' : 'online');
          setOpen(true);
        }}
      >
        {t('shop.buy')}
      </Button>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t('shop.confirm.title', { name: pass.name })}
        description={
          paying === 'online'
            ? t('shop.confirm.bodyOnline', { amount: fmt.money(pass.total), venue: shop.venue.name })
            : t('shop.confirm.body', { amount: fmt.money(pass.total), venue: shop.venue.name, left: fmt.money(left) })
        }
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t('shop.confirm.cancel')}
            </Button>
            <Button
              variant="primary"
              busy={buy.isPending}
              onClick={() =>
                buy.mutate(
                  { pass, key, tender: paying },
                  {
                    onSuccess: (bought) => {
                      if (bought.status === 'awaiting_payment' && bought.payment !== null) {
                        goToCheckout({ id: bought.payment.id, purpose: 'pass', name: pass.name, venueSlug: shop.venue.slug }, bought.payment.checkoutUrl);
                        return;
                      }
                      show({ title: bought.queuedSeconds > 0 ? t('shop.doneSession', { name: bought.name }) : t('shop.done', { name: bought.name }), tone: 'ok' });
                      setOpen(false);
                    },
                    onError: () => setOpen(false),
                  },
                )
              }
            >
              {paying === 'online' ? t('shop.confirm.payOnline', { amount: fmt.money(pass.total) }) : t('shop.confirm.pay', { amount: fmt.money(pass.total) })}
            </Button>
          </>
        }
      >
        {shop.onlinePayments && shop.walletEnabled ? (
          <RadioGroup
            label={t('shop.pay.legend')}
            value={tender}
            onValueChange={(value) => {
              // A different way of paying is a different request: it needs its own key.
              setKey(newKey());
              setTender(value === 'wallet' ? 'wallet' : 'online');
            }}
            options={[
              {
                value: 'wallet',
                disabled: !pass.affordable,
                label: pass.affordable
                  ? `${t('shop.pay.wallet')} (${t('shop.pay.walletBalance', { amount: fmt.money(shop.walletBalance) })})`
                  : `${t('shop.pay.wallet')} (${t('shop.pay.walletShort')})`,
              },
              { value: 'online', label: t('shop.pay.online') },
            ]}
          />
        ) : null}
      </Dialog>
    </>
  );
}
/** "Your wallet here: ₹470.00", above the passes it pays for. */
export function ShopWalletLine({ shop }: { readonly shop: MyVenuePassShop }): ReactNode {
  const { t } = useTranslation('player');
  return <p className="pw-shop__wallet clav-num">{t('shop.wallet', { amount: formatterFor(shop.walletBalance.currency).money(shop.walletBalance) })}</p>;
}

/**
 * Play's "Buy a pass" (spec/15 P-10): the passes the player can buy at the venue they are playing at, from their wallet
 * there. One for this PC joins the session at once; another waits in their passes, and its card says so first.
 */
export function PassesToBuy({ live }: { readonly live: MyLiveSession }): ReactNode {
  const { t } = useTranslation('player');
  const shop = useMyVenuePassShop(live.venue.id);
  if (shop.isPending || shop.isError) return null;
  const data = shop.data;
  return (
    <section className="pw-panel" aria-labelledby="pw-buy-pass">
      <SectionHead id="pw-buy-pass" title={t('shop.title')} />
      {!data.walletEnabled && !data.onlinePayments ? (
        <p className="pw-muted">{t('shop.walletOff')}</p>
      ) : data.passes.length === 0 ? (
        <p className="pw-muted">{t('shop.none')}</p>
      ) : (
        <>
          {data.walletEnabled ? <ShopWalletLine shop={data} /> : null}
          <ul className="pw-shop">
            {data.passes.map((pass) => (
              <li key={pass.id} className="pw-shop__pass">
                <div className="pw-shop__top">
                  <h3 className="pw-shop__name">{pass.name}</h3>
                  <span className="pw-shop__price clav-num">{formatterFor(pass.total.currency).money(pass.total)}</span>
                </div>
                <p className="pw-shop__play">{t('venue.passes.play', { duration: shortDuration(pass.durationSeconds) })}</p>
                <ul className="pw-shop__facts">
                  {pass.window === '' ? null : <li>{t('venue.passes.window', { window: pass.window })}</li>}
                  {pass.validity === '' ? null : <li>{t('venue.passes.validity', { validity: pass.validity })}</li>}
                  <li>{pass.forYourPc ? t('shop.forYourPc') : t('shop.notForYourPc')}</li>
                </ul>
                <div className="pw-shop__buy">
                  <BuyPassAction shop={data} pass={pass} />
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
